import { useState, useEffect } from 'react';
import { PushNotificationService } from '../services/pushNotificationService';
import { getPortfolios, Portfolios } from '../storage';
import { validateNotificationPreferences } from '../utils/notificationHelpers';
import { FREE_SMART_SUMMARY_LIMIT } from '../constants/freemium';
import * as stores from '../stores';

export interface NotificationPreference {
  id: string;
  description: string;
  enabled: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreference[] = [
  // Top 3 shown by default in onboarding
  {
    id: 'smart_notifications',
    description: 'Daily market update',
    enabled: false,
  },
  {
    id: 'news_alerts',
    description: 'News for stock price movements',
    enabled: false,
  },
  {
    id: 'daily_alerts',
    description: 'Daily change +- 5%',
    enabled: false,
  },
  {
    id: 'ai_portfolio_trades',
    description: 'AI portfolio trades',
    enabled: false,
  },
  // Hidden behind expandable in onboarding
  {
    id: 'weekly_alerts',
    description: 'Weekly change +- 10%',
    enabled: false,
  },
  {
    id: 'insider_sales',
    description: 'Insider sales above $100M',
    enabled: false,
  },
  {
    id: 'rsi_alerts',
    description: 'Overbought or oversold (RSI)',
    enabled: false,
  },
  {
    id: 'sma_alerts',
    description: 'Crosses 50d or 200d SMA',
    enabled: false,
  },
  {
    id: 'insight_alerts',
    description: 'Earnings call insights',
    enabled: false,
  },
  {
    id: 'earnings_alerts',
    description: 'Earnings Transcripts',
    enabled: false,
  },
];

/**
 * Merge saved preferences with default preferences
 * This ensures new notification types are added while preserving user settings
 */
const mergePreferencesWithDefaults = (savedPreferences: NotificationPreference[] | null): NotificationPreference[] => {
  if (!savedPreferences || !validateNotificationPreferences(savedPreferences)) {
    console.warn('[PREFERENCES] Invalid saved preferences, using defaults');
    return DEFAULT_PREFERENCES;
  }

  // Create a map of saved preferences for easy lookup
  const savedPrefsMap = new Map(savedPreferences.map((pref) => [pref.id, pref]));

  // Merge: use saved preference if exists, otherwise use default
  const mergedPreferences = DEFAULT_PREFERENCES.map((defaultPref) => {
    const savedPref = savedPrefsMap.get(defaultPref.id);
    if (savedPref) {
      // Keep user's enabled/disabled choice but update description in case it changed
      return {
        ...defaultPref,
        enabled: savedPref.enabled,
      };
    }
    // New preference - use default
    return defaultPref;
  });

  // Save the merged preferences back to localStorage
  try {
    localStorage.setItem('notificationPreferences', JSON.stringify(mergedPreferences));
  } catch (error) {
    console.error('[PREFERENCES] Failed to save merged preferences:', error);
  }

  console.log('[PREFERENCES] Merged preferences:', {
    savedCount: savedPreferences.length,
    defaultCount: DEFAULT_PREFERENCES.length,
    mergedCount: mergedPreferences.length,
    newFields: DEFAULT_PREFERENCES.filter((def) => !savedPrefsMap.has(def.id)).map((f) => f.id),
  });

  return mergedPreferences;
};

export const useNotificationPreferences = () => {
  const [watchlistSymbols, setWatchlistSymbols] = useState<string[]>([]);
  const [isNotificationBlockedModalOpen, setIsNotificationBlockedModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false); // Background sync indicator

  // Check if user is Pro
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isPro = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Subscribe to portfolio changes and load symbols from all portfolios
  useEffect(() => {
    // Subscribe to portfolio changes - this ensures notification preferences
    // are updated whenever portfolios are modified
    const unsubscribe = Portfolios.subscribe(
      (state) => state.portfolios,
      (portfolios) => {
        // Get all unique symbols across all portfolios
        const allSymbols = Array.from(new Set(portfolios.flatMap((portfolio) => portfolio.symbols || [])));

        // Update state with all symbols (used for notifications)
        setWatchlistSymbols(allSymbols);
      },
    );

    // Initial load of symbols from all portfolios
    const loadSymbols = async () => {
      try {
        const portfolios = await getPortfolios();

        const allSymbols = Array.from(new Set(portfolios.flatMap((portfolio) => portfolio.symbols || [])));

        setWatchlistSymbols(allSymbols);
      } catch (error) {
        console.error('Failed to load portfolios:', error);
        setError('Failed to load portfolio data');
      }
    };
    loadSymbols();

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const [preferences, setPreferences] = useState<NotificationPreference[]>(() => {
    try {
      const savedPreferences = localStorage.getItem('notificationPreferences');
      const parsedPreferences = savedPreferences ? JSON.parse(savedPreferences) : null;
      return mergePreferencesWithDefaults(parsedPreferences);
    } catch (error) {
      console.error('[PREFERENCES] Failed to load preferences from localStorage:', error);
      return DEFAULT_PREFERENCES;
    }
  });

  const areAllEnabled = preferences.every((pref) => {
    // If user is not Pro, don't consider smart_notifications for "all enabled" state
    if (pref.id === 'smart_notifications' && !isPro) {
      return true; // Always consider it as "enabled" for the toggle all logic
    }
    return pref.enabled;
  });

  const clearError = () => setError(null);

  // Check if user has reached free smart summary limit
  const hasReachedFreeSummaryLimit = (): boolean => {
    try {
      if (isPro) return false; // Pro users have unlimited access
      const viewed = localStorage.getItem('smartSummariesViewed');
      if (viewed) {
        const parsed = parseInt(viewed, 10);
        const viewedCount = !isNaN(parsed) ? parsed : 0;
        return viewedCount >= FREE_SMART_SUMMARY_LIMIT;
      }
      return false;
    } catch (error) {
      console.warn('[Freemium] Error checking summary limit:', error);
      return false; // Default to allowing access on error
    }
  };

  // Background sync function - doesn't block UI
  const syncInBackground = async (updatedPrefs: NotificationPreference[], isEnabling: boolean) => {
    setIsSyncing(true);
    setError(null);

    // Set a maximum timeout for sync indicator
    const syncTimeout = setTimeout(() => {
      setIsSyncing(false);
    }, 5000); // Max 5 seconds

    try {
      if (isEnabling) {
        const currentPermission = await PushNotificationService.checkPermissions();
        if (currentPermission === 'denied') {
          setIsNotificationBlockedModalOpen(true);
          // Revert the UI change since permission was denied
          const revertedPrefs = updatedPrefs.map((pref) => ({ ...pref, enabled: false }));
          setPreferences(revertedPrefs);
          localStorage.setItem('notificationPreferences', JSON.stringify(revertedPrefs));
          clearTimeout(syncTimeout);
          setIsSyncing(false);
          return;
        }
      }

      const enabledTypes = updatedPrefs.filter((p) => p.enabled).map((p) => p.id);

      // Always sync with backend
      try {
        // If there are enabled types, initialize and check permissions
        if (enabledTypes.length > 0) {
          const permissionGranted = await PushNotificationService.initialize({
            notificationTypes: enabledTypes,
            watchlistSymbols,
          });

          if (!permissionGranted) {
            const disabled = updatedPrefs.map((pref) => ({ ...pref, enabled: false }));
            setPreferences(disabled);
            localStorage.setItem('notificationPreferences', JSON.stringify(disabled));
            setIsNotificationBlockedModalOpen(true);
            clearTimeout(syncTimeout);
            setIsSyncing(false);
            return;
          }
        }

        // Always call updateConfig to sync state (enabled or disabled)
        await PushNotificationService.updateConfig({
          notificationTypes: enabledTypes,
          watchlistSymbols,
        });

        console.log('[DEBUG] Successfully synced notification preferences in background');
      } catch (syncError) {
        console.error('[DEBUG] Failed to sync notifications with backend:', syncError);

        // Set user-friendly error message but don't revert UI
        if (syncError instanceof Error) {
          setError(`Sync failed: ${syncError.message}. Changes saved locally.`);
        } else {
          setError('Sync failed. Changes saved locally and will retry automatically.');
        }
      }
    } catch (error) {
      console.error('[DEBUG] Error in background sync:', error);
    } finally {
      clearTimeout(syncTimeout);
      setIsSyncing(false);
    }
  };

  const handleToggle = async (id: string): Promise<void> => {
    console.log(`[DEBUG] handleToggle called for notification type: ${id}`);
    console.log('[DEBUG] Current portfolio symbols:', watchlistSymbols);

    const currentPref = preferences.find((pref) => pref.id === id);
    const isEnabling = !currentPref?.enabled;

    // Check if user is trying to enable smart_notifications
    if (id === 'smart_notifications' && isEnabling && !isPro) {
      // For non-Pro users, only block if they've reached the 3 free summary limit
      if (hasReachedFreeSummaryLimit()) {
        console.log('[Freemium] User reached free summary limit, showing paywall');
        stores.showPaymentModalByPreference('smart_notifications_toggle_limit_reached');
        return;
      }
      // If they haven't reached the limit, allow them to enable (they have free summaries left)
      console.log('[Freemium] Non-Pro user enabling smart notifications with free summaries available');
    }

    // Update UI immediately
    const newPreferences = preferences.map((pref) => (pref.id === id ? { ...pref, enabled: !pref.enabled } : pref));

    setPreferences(newPreferences);

    // Save to localStorage immediately
    try {
      localStorage.setItem('notificationPreferences', JSON.stringify(newPreferences));
    } catch (storageError) {
      console.warn('[PREFERENCES] Failed to save to localStorage:', storageError);
    }

    // Sync in background without blocking UI
    syncInBackground(newPreferences, isEnabling);
  };

  const handleToggleAll = async (): Promise<void> => {
    const isEnabling = !areAllEnabled;

    // Update UI immediately
    const newPreferences = preferences.map((pref) => {
      // For smart_notifications: only exclude if user is not Pro AND has reached the free limit
      if (pref.id === 'smart_notifications' && isEnabling && !isPro) {
        // Allow enabling if user hasn't reached the 3 free summary limit
        if (hasReachedFreeSummaryLimit()) {
          return { ...pref, enabled: false }; // Keep disabled if limit reached
        }
      }
      return { ...pref, enabled: isEnabling };
    });

    setPreferences(newPreferences);

    // Save to localStorage immediately
    try {
      localStorage.setItem('notificationPreferences', JSON.stringify(newPreferences));
    } catch (storageError) {
      console.warn('[PREFERENCES] Failed to save to localStorage:', storageError);
    }

    // Sync in background without blocking UI
    syncInBackground(newPreferences, isEnabling);
  };

  return {
    preferences,
    handleToggle,
    handleToggleAll,
    areAllEnabled,
    watchlistSymbols,
    isNotificationBlockedModalOpen,
    setIsNotificationBlockedModalOpen,
    isSyncing, // Background sync indicator instead of isLoading
    error,
    clearError,
    hasReachedFreeSummaryLimit, // Expose for use in components
  };
};
